import { App, Button, Flex, Space, Tag, Typography } from 'antd';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { formatDate } from '@/shared/lib/date';
import { translateApiError } from '@/shared/api/errorToMessage';
import {
  useAcceptOrderRequestMutation,
  useCreateSaleFromOrderRequestMutation,
  useGetOrderRequestQuery,
  useRejectOrderRequestMutation,
} from '@/entities/order-request';
import type { OrderRequestLine, OrderRequestStatus } from '@/entities/order-request';
import { useWindowManager } from '@/shared/lib/windows';
import { useHotkeyActions } from '@/shared/lib/hotkeys';
import { useBreakpoint } from '@/shared/lib/hooks';
import { HotkeyButton, HotkeyTip } from '@/shared/ui/hotkey';
import { ResponsiveTable } from '@/shared/ui/ResponsiveTable';
import type { ResponsiveColumns } from '@/shared/ui/ResponsiveTable';

const STATUS_LABEL: Record<OrderRequestStatus, string> = { NEW: 'Новая', ACCEPTED: 'Принята', REJECTED: 'Отклонена', DONE: 'Создана продажа' };
const STATUS_COLOR: Record<OrderRequestStatus, string> = { NEW: 'blue', ACCEPTED: 'green', REJECTED: 'red', DONE: 'default' };

export interface OrderRequestWindowProps {
  id: string;
  onClose: () => void;
}

/** §10: заявка на товар — просмотр, принять/отклонить, создать продажу из принятой заявки. */
export function OrderRequestWindow({ id, onClose }: OrderRequestWindowProps) {
  const { message, modal } = App.useApp();
  const { isMobile } = useBreakpoint();
  const windows = useWindowManager();
  const { data: request, isFetching } = useGetOrderRequestQuery(id);
  const [accept, { isLoading: isAccepting }] = useAcceptOrderRequestMutation();
  const [reject, { isLoading: isRejecting }] = useRejectOrderRequestMutation();
  const [createSale, { isLoading: isCreatingSale }] = useCreateSaleFromOrderRequestMutation();

  const columns: ResponsiveColumns<OrderRequestLine> = [
    {
      title: 'Товар',
      dataIndex: ['category', 'name'],
      render: (name: string, line) =>
        line.category.isArchived ? (
          <span>
            {name} <Tag color="warning">архив</Tag>
          </span>
        ) : (
          name
        ),
      mobile: 'title',
    },
    { title: 'Коробок', dataIndex: 'boxes', width: 90, align: 'right' },
    { title: 'Комментарий', dataIndex: 'comment', render: (v: string | null) => (isMobile && v ? `Комментарий: ${v}` : (v ?? '')), mobile: 'subtitle' },
  ];

  async function handleAccept() {
    if (busy) return;
    try {
      await accept(id).unwrap();
      message.success('Заявка принята');
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const busy = isAccepting || isRejecting || isCreatingSale;

  /** Отклонение не вернуть, поэтому всегда переспрашиваем, а фокус в диалоге — на «Не отклонять». */
  function handleReject() {
    if (busy) return;
    modal.confirm({
      title: 'Отклонить заявку?',
      okText: 'Отклонить',
      cancelText: 'Не отклонять',
      okButtonProps: { danger: true },
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          await reject(id).unwrap();
          message.success('Заявка отклонена');
        } catch (e) {
          message.error(translateApiError(e));
        }
      },
    });
  }

  async function handleCreateSale() {
    if (busy) return;
    try {
      const res = await createSale(id).unwrap();
      if (res.warnings.length > 0) {
        message.warning('Черновик продажи создан с предупреждениями: в заявке есть архивные товары — замените их перед проведением');
      } else {
        message.success('Создан черновик продажи');
      }
      windows.open({ kind: 'document', type: 'SALE', id: res.id });
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const openSale = () => request?.convertedDocumentId && windows.open({ kind: 'document', type: 'SALE', id: request.convertedDocumentId });
  const actions = useHotkeyActions(
    request?.status === 'NEW'
      ? {
          'request.accept': { run: () => void handleAccept() },
          'request.reject': { hint: false, run: handleReject },
        }
      : request?.status === 'ACCEPTED'
        ? { 'request.sale': { label: 'Создать продажу', run: () => void handleCreateSale() } }
        : request?.status === 'DONE' && request.convertedDocumentId
          ? { 'request.sale': { label: 'Открыть продажу', run: () => void openSale() } }
          : {},
  );

  return (
    <FloatingWindow
      kind="order-request"
      title={request ? `Заявка от ${request.counterparty?.name ?? '—'}` : 'Заявка на товар'}
      onClose={onClose}
      footer={renderFooter()}
      hotkeys={actions.bindings}
      initialFocus="window"
    >
      {isFetching && !request ? (
        <Typography.Text type="secondary">Загрузка…</Typography.Text>
      ) : request ? (
        <Flex vertical gap={8}>
          <Flex gap={isMobile ? 8 : 16} align="center" wrap={isMobile ? 'wrap' : undefined}>
            <Typography.Text style={{ fontSize: isMobile ? 14 : 12 }}>Дата: {formatDate(request.createdAt)}</Typography.Text>
            <Tag color={STATUS_COLOR[request.status]}>{STATUS_LABEL[request.status]}</Tag>
          </Flex>
          {request.comment ? <Typography.Text style={{ fontSize: isMobile ? 14 : 12 }}>Комментарий: {request.comment}</Typography.Text> : null}
          <ResponsiveTable<OrderRequestLine> size="small" rowKey="id" dataSource={request.lines} columns={columns} pagination={false} />
        </Flex>
      ) : null}
    </FloatingWindow>
  );

  function renderFooter() {
    if (!request) return null;
    const mobileButton = isMobile ? ({ size: 'large', block: true } as const) : {};
    const tipDisplay = isMobile ? 'block' : undefined;
    const buttons = (
      <>
        {request.status === 'NEW' ? (
          <>
            <HotkeyButton type="primary" loading={isAccepting} action={actions.get('request.accept')} tipDisplay={tipDisplay} {...mobileButton}>
              Принять
            </HotkeyButton>
            <HotkeyButton danger loading={isRejecting} action={actions.get('request.reject')} tipDisplay={tipDisplay} {...mobileButton}>
              Отклонить
            </HotkeyButton>
          </>
        ) : null}
        {request.status === 'ACCEPTED' ? (
          <HotkeyButton type="primary" loading={isCreatingSale} action={actions.get('request.sale')} tipDisplay={tipDisplay} {...mobileButton}>
            Создать продажу
          </HotkeyButton>
        ) : null}
        {request.status === 'DONE' && request.convertedDocumentId ? (
          <HotkeyButton action={actions.get('request.sale')} tipDisplay={tipDisplay} {...mobileButton}>
            Открыть продажу
          </HotkeyButton>
        ) : null}
        <HotkeyTip catalogId="window.close" display={tipDisplay}>
          <Button onClick={onClose} {...mobileButton}>
            Закрыть
          </Button>
        </HotkeyTip>
      </>
    );
    // Телефон: действия внизу одной колонкой, крупными кнопками на всю ширину.
    return isMobile ? (
      <Flex vertical gap={8} style={{ width: '100%' }}>
        {buttons}
      </Flex>
    ) : (
      <Space wrap>{buttons}</Space>
    );
  }
}
